import firebase from '../config/firebase.config';

const database = firebase.firestore();

/**
 * Team-level holiday overrides. Stored as a sparse map keyed by date, kept
 * separate from the company's holiday list so a team never has to duplicate
 * the full company list just to add or drop one date:
 * - overrides[date] = holiday object -> this team's own version of that date
 *   (used both for editing a company holiday and adding a team-only one).
 * - overrides[date] = null           -> this team doesn't observe that date.
 * A date absent from the map simply inherits the company's holiday list live.
 */

export async function fetchTeamHolidayOverrides(teamId, year) {
  if (!teamId) return {};
  const snap = await database
    .collection('teamDatabase')
    .doc(teamId)
    .collection('holidayOverrides')
    .doc(year.toString())
    .get();
  return snap.data()?.overrides || {};
}

/**
 * Writes one or more date -> holiday|null overrides in a single call, so an
 * operation spanning multiple dates (e.g. moving a team's holiday to a new
 * date - clear the old one, claim the new one) is one Firestore write
 * instead of several sequential ones.
 * @param {Object<string, {date: string, name: string, optional?: boolean}|null>} updates
 */
export async function setTeamHolidayOverrides({
  teamId,
  year,
  updates,
  updatedBy,
}) {
  const payload = {
    year,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedBy,
  };
  Object.entries(updates).forEach(([date, holiday]) => {
    payload[`overrides.${date}`] = holiday;
  });

  await database
    .collection('teamDatabase')
    .doc(teamId)
    .collection('holidayOverrides')
    .doc(year.toString())
    .set(payload, { merge: true });
}
